/**
 * VerifyCard — AOS-LID-02: Staff QR Verification Interface
 *
 * Route: /campus/verify  (registrar, finance_officer, finance_manager,
 *                          programme_manager, institution_admin, super_admin)
 *
 * Flow:
 *   1. Staff opens screen → camera activates (with permission prompt)
 *   2. Student displays Digital ID QR
 *   3. Staff scans → POST /api/campus/id/verify
 *   4. Full-screen result: ALLOWED | DENIED | UNAVAILABLE
 *   5. "Next Scan" resets the scanner
 *
 * Security:
 *  - Requires campus.id.verify permission
 *  - Token consumed atomically server-side (no replay)
 *  - Manual input fallback for when camera is unavailable
 *  - Every verification is audited server-side
 *  - Does NOT automatically modify hostel booking status
 */
import React, {
  useCallback, useEffect, useRef, useState,
} from "react";
import { Html5Qrcode } from "html5-qrcode";
import {
  AlertCircle, AlertTriangle, Camera, CameraOff, CheckCircle2,
  ClipboardPaste, Loader2, RefreshCw, ScanLine, Shield, User,
  XCircle, BookOpen
} from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";

const PERMITTED_ROLES = new Set([
  "registrar", "finance_officer", "finance_manager",
  "programme_manager", "institution_admin", "super_admin",
]);

const CAMERA_ID = "lid-qr-scanner";

// ─── Result card ─────────────────────────────────────────────────────────────
function ResultCard({ result, onNextScan }) {
  const isAllowed    = result.result === "ALLOWED";
  const isDenied     = result.result === "DENIED";
  const isUnavailable = result.result === "UNAVAILABLE";

  const cfg = isAllowed
    ? { bg: "from-emerald-950 to-emerald-900", border: "border-emerald-700/60",
        icon: <CheckCircle2 className="w-16 h-16 text-emerald-400" />,
        title: "ALLOWED", titleColor: "text-emerald-400", badge: "bg-emerald-500/20 text-emerald-300" }
    : isDenied
    ? { bg: "from-red-950 to-red-900", border: "border-red-700/60",
        icon: <XCircle className="w-16 h-16 text-red-400" />,
        title: "DENIED", titleColor: "text-red-400", badge: "bg-red-500/20 text-red-300" }
    : { bg: "from-amber-950 to-amber-900", border: "border-amber-700/60",
        icon: <AlertTriangle className="w-16 h-16 text-amber-400" />,
        title: "UNAVAILABLE", titleColor: "text-amber-400", badge: "bg-amber-500/20 text-amber-300" };

  const student = result.student;
  const hostel  = result.hostel;

  return (
    <div
      className={`w-full max-w-sm rounded-3xl border ${cfg.border} overflow-hidden shadow-2xl`}
      style={{ background: `linear-gradient(160deg, var(--tw-gradient-stops))` }}
      data-testid="verify-result-card"
    >
      {/* Result banner */}
      <div
        className={`bg-gradient-to-br ${cfg.bg} px-6 py-8 flex flex-col items-center gap-3 border-b ${cfg.border}`}
        data-testid={`result-${result.result?.toLowerCase()}`}
      >
        {cfg.icon}
        <h1 className={`text-4xl font-black tracking-tight ${cfg.titleColor}`}>
          {cfg.title}
        </h1>
        {result.reason && (
          <p className="text-white/60 text-sm text-center leading-snug max-w-xs">
            {result.reason}
          </p>
        )}
      </div>

      {/* Student details (shown when identity valid) */}
      {student && (
        <div className="bg-slate-900 px-5 py-4 flex items-center gap-3">
          <div className="w-10 h-10 rounded-full bg-slate-700 flex items-center justify-center shrink-0">
            <User className="w-5 h-5 text-slate-400" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-semibold text-sm truncate" data-testid="result-student-name">
              {student.display_name || `${student.given_name} ${student.family_name}`}
            </p>
            <p className="text-slate-500 text-xs font-mono">{student.student_number}</p>
          </div>
          <span className={`ml-auto text-[10px] font-bold uppercase px-2 py-0.5 rounded-full ${cfg.badge}`}>
            {student.lifecycle_status}
          </span>
        </div>
      )}

      {/* Hostel details (shown when ALLOWED for hostel service) */}
      {hostel && isAllowed && (
        <div className="bg-slate-900/50 px-5 py-3 border-t border-white/5">
          <p className="text-emerald-400 text-xs font-semibold" data-testid="result-hostel">
            {hostel.hostel_name} · Room {hostel.room_number}
          </p>
          <p className="text-slate-500 text-[10px]">
            {new Date(hostel.check_in_date).toLocaleDateString()} →{" "}
            {new Date(hostel.check_out_date).toLocaleDateString()}
          </p>
        </div>
      )}

      {/* Checks summary */}
      {result.checks && Object.keys(result.checks).length > 0 && (
        <CheckSummary checks={result.checks} />
      )}

      {/* Timestamp */}
      <div className="bg-slate-900 px-5 py-2 border-t border-white/5 flex items-center justify-between">
        <p className="text-slate-600 text-[10px] font-mono">
          {result.verified_at ? new Date(result.verified_at).toLocaleTimeString() : ""}
        </p>
        <p className="text-slate-600 text-[10px] truncate max-w-[150px]">
          {result.correlation_id?.slice(0, 8)}
        </p>
      </div>

      {/* Next scan button */}
      <div className="bg-slate-950 px-5 py-4">
        <button
          onClick={onNextScan}
          className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
          data-testid="next-scan-btn"
        >
          <ScanLine className="w-4 h-4" />
          Next Scan
        </button>
      </div>
    </div>
  );
}

function CheckSummary({ checks }) {
  const items = [
    { key: "signature_valid",   label: "Signature" },
    { key: "identity_valid",    label: "Identity" },
    { key: "enrollment_valid",  label: "Enrolment" },
    { key: "hostel_entitlement", label: "Hostel" },
    { key: "card_suspended",    label: "Suspended", invert: true },
  ].filter((i) => i.key in checks);

  if (!items.length) return null;

  return (
    <div className="bg-slate-900/70 px-5 py-3 border-t border-white/5 flex flex-wrap gap-2">
      {items.map(({ key, label, invert }) => {
        const val = checks[key];
        const pass = invert ? !val : !!val;
        return (
          <span
            key={key}
            className={`text-[10px] font-semibold px-2 py-0.5 rounded-full ${
              pass ? "bg-emerald-900/40 text-emerald-400" : "bg-red-900/40 text-red-400"
            }`}
            data-testid={`check-${key}`}
          >
            {pass ? "✓" : "✗"} {label}
          </span>
        );
      })}
    </div>
  );
}

// ─── Audit History ────────────────────────────────────────────────────────────
function AuditHistory() {
  const [events, setEvents] = useState(null);
  const [loading, setLoading] = useState(false);
  const [open, setOpen] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data: d } = await api.get("/campus/id/verify/history?limit=20");
      setEvents(d.events || []);
    } catch {
      setEvents([]);
    } finally {
      setLoading(false);
    }
  };

  const toggle = () => {
    setOpen((v) => !v);
    if (!open && !events) load();
  };

  const resultColor = {
    ALLOWED: "text-emerald-400",
    DENIED: "text-red-400",
    REJECTED: "text-amber-400",
  };

  return (
    <div className="w-full max-w-sm mt-4" data-testid="audit-history-section">
      <button
        onClick={toggle}
        className="w-full flex items-center justify-between text-slate-500 text-xs py-2 px-3 rounded-lg hover:bg-white/5 transition-colors"
        data-testid="audit-history-toggle"
      >
        <span className="flex items-center gap-1.5">
          <BookOpen className="w-3 h-3" /> Recent Verifications
        </span>
        <span>{open ? "▲" : "▼"}</span>
      </button>

      {open && (
        <div className="mt-1 bg-slate-900 border border-white/10 rounded-xl overflow-hidden">
          {loading ? (
            <div className="p-4 flex justify-center">
              <Loader2 className="w-5 h-5 text-sky-400 animate-spin" />
            </div>
          ) : !events?.length ? (
            <p className="text-slate-600 text-xs text-center p-4">No recent verifications</p>
          ) : (
            <div className="divide-y divide-white/5">
              {events.map((ev) => (
                <div key={ev.id} className="px-3 py-2 flex items-center justify-between gap-2">
                  <div className="min-w-0">
                    <p className="text-slate-400 text-[10px] font-mono truncate">{ev.student_id?.slice(0, 8)}…</p>
                    <p className="text-slate-600 text-[9px]">{ev.staff_email}</p>
                  </div>
                  <div className="text-right shrink-0">
                    <p className={`text-[10px] font-bold ${resultColor[ev.outcome] || "text-slate-400"}`}>
                      {ev.outcome}
                    </p>
                    <p className="text-slate-600 text-[9px]">
                      {ev.occurred_at ? new Date(ev.occurred_at).toLocaleTimeString() : ""}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Manual Input ─────────────────────────────────────────────────────────────
function ManualInput({ onSubmit, loading }) {
  const [value, setValue] = useState("");

  const submit = (e) => {
    e.preventDefault();
    const v = value.trim();
    if (v) onSubmit(v);
  };

  return (
    <form
      onSubmit={submit}
      className="w-full flex gap-2"
      data-testid="manual-input-form"
    >
      <input
        value={value}
        onChange={(e) => setValue(e.target.value)}
        placeholder="Paste QR token JSON…"
        className="flex-1 bg-slate-800 border border-white/10 rounded-lg text-white text-xs px-3 py-2 placeholder:text-slate-600 focus:outline-none focus:ring-1 focus:ring-sky-500"
        data-testid="manual-token-input"
      />
      <button
        type="submit"
        disabled={!value.trim() || loading}
        className="bg-sky-600 hover:bg-sky-500 disabled:opacity-50 text-white text-xs font-semibold px-3 py-2 rounded-lg flex items-center gap-1 transition-colors"
        data-testid="manual-verify-btn"
      >
        {loading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Shield className="w-3 h-3" />}
        Verify
      </button>
    </form>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────
export default function VerifyCard() {
  const { user } = useAuth();

  const [scanState, setScanState] = useState("idle"); // idle | scanning | verifying | result | error
  const [result, setResult]     = useState(null);
  const [cameraError, setCameraError] = useState(null);
  const [verifying, setVerifying]     = useState(false);
  const [service, setService]         = useState("HOSTEL");

  const scannerRef = useRef(null);
  const lastScanRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    return () => { isMounted.current = false; };
  }, []);

  // ── Verify token ────────────────────────────────────────────────────────────
  const verifyToken = useCallback(async (tokenStr) => {
    if (verifying) return;
    setVerifying(true);
    setScanState("verifying");
    try {
      const { data } = await api.post("/campus/id/verify", {
        token: tokenStr,
        service,
      });
      if (isMounted.current) {
        setResult(data);
        setScanState("result");
      }
    } catch (err) {
      if (isMounted.current) {
        const detail = err?.response?.data?.detail;
        const msg = typeof detail === "object" ? JSON.stringify(detail) :
                    typeof detail === "string" ? detail : "Verification failed";
        setResult({
          result: "UNAVAILABLE",
          reason: msg,
          checks: {},
          student: null,
          hostel: null,
          verified_at: new Date().toISOString(),
          correlation_id: null,
        });
        setScanState("result");
      }
    } finally {
      if (isMounted.current) setVerifying(false);
    }
  }, [verifying, service]);

  // ── Camera ──────────────────────────────────────────────────────────────────
  const startCamera = useCallback(async () => {
    setCameraError(null);
    setScanState("scanning");
    lastScanRef.current = null;

    await new Promise((r) => setTimeout(r, 100)); // let DOM settle

    const scanner = new Html5Qrcode(CAMERA_ID, { verbose: false });
    scannerRef.current = scanner;

    try {
      await scanner.start(
        { facingMode: "environment" },
        { fps: 8, qrbox: { width: 220, height: 220 }, aspectRatio: 1 },
        (decodedText) => {
          // De-bounce: ignore duplicate scan within 2s
          if (lastScanRef.current === decodedText) return;
          lastScanRef.current = decodedText;
          stopCamera(scanner);
          verifyToken(decodedText);
        },
        () => {}, // ignore scan errors
      );
    } catch (err) {
      const msg = String(err).includes("permission")
        ? "Camera permission denied. Use manual input below."
        : "Camera unavailable. Use manual input below.";
      setCameraError(msg);
      setScanState("error");
    }
  }, [verifyToken]);

  const stopCamera = useCallback(async (sc) => {
    const s = sc || scannerRef.current;
    if (!s) return;
    try {
      if (s.isScanning) await s.stop();
      s.clear();
    } catch { /* ignore */ }
    scannerRef.current = null;
  }, []);

  const handleReset = useCallback(async () => {
    await stopCamera();
    setResult(null);
    setScanState("idle");
    setCameraError(null);
    lastScanRef.current = null;
  }, [stopCamera]);

  // Cleanup on unmount
  useEffect(() => {
    return () => { stopCamera(); };
  }, [stopCamera]);

  const isPermitted = PERMITTED_ROLES.has(user?.role);

  return (
    <div
      className="min-h-screen bg-slate-950 flex flex-col items-center justify-start py-6 px-4"
      data-testid="verify-card-page"
    >
      {!isPermitted ? (
        <div className="flex items-center justify-center w-full min-h-screen">
          <div className="max-w-sm w-full bg-slate-900 border border-red-800/50 rounded-2xl p-6 text-center">
            <Shield className="w-12 h-12 text-red-400 mx-auto mb-3" />
            <h2 className="text-white font-semibold">Access Denied</h2>
            <p className="text-slate-400 text-sm mt-1">
              You do not have permission to verify campus IDs.
            </p>
          </div>
        </div>
      ) : (
      <>
      {/* Header */}
      <div className="w-full max-w-sm mb-4 flex items-center justify-between">
        <div>
          <h1 className="text-white font-bold text-lg flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-sky-400" />
            Verify Campus ID
          </h1>
          <p className="text-slate-500 text-xs mt-0.5">Scan student QR to check entitlement</p>
        </div>
        {/* Service selector */}
        <select
          value={service}
          onChange={(e) => setService(e.target.value)}
          className="bg-slate-800 border border-white/10 text-white text-xs rounded-lg px-2 py-1.5 focus:outline-none focus:ring-1 focus:ring-sky-500"
          data-testid="service-selector"
        >
          <option value="HOSTEL">Hostel</option>
          <option value="CAMPUS">Campus</option>
          <option value="LIBRARY">Library</option>
        </select>
      </div>

      {/* Result view */}
      {scanState === "result" && result ? (
        <ResultCard result={result} onNextScan={handleReset} />
      ) : (
        <>
          {/* Scanner area */}
          <div
            className="w-full max-w-sm bg-slate-900 border border-white/10 rounded-3xl overflow-hidden"
            data-testid="scanner-area"
          >
            {/* Camera viewport */}
            <div className="relative bg-slate-950 aspect-square rounded-t-3xl overflow-hidden">
              {/* QR scanner DOM target */}
              <div id={CAMERA_ID} className="absolute inset-0" />

              {/* Overlay when not scanning */}
              {scanState !== "scanning" && (
                <div className="absolute inset-0 flex flex-col items-center justify-center gap-3">
                  {scanState === "verifying" ? (
                    <>
                      <Loader2 className="w-12 h-12 text-sky-400 animate-spin" />
                      <p className="text-slate-400 text-sm">Verifying…</p>
                    </>
                  ) : scanState === "error" ? (
                    <>
                      <CameraOff className="w-12 h-12 text-slate-600" />
                      <p className="text-slate-500 text-sm text-center px-4">
                        {cameraError || "Camera unavailable"}
                      </p>
                    </>
                  ) : (
                    <>
                      <div className="w-48 h-48 rounded-xl border-2 border-dashed border-slate-700 flex items-center justify-center">
                        <Camera className="w-10 h-10 text-slate-600" />
                      </div>
                      <p className="text-slate-600 text-xs">Tap Start to activate camera</p>
                    </>
                  )}
                </div>
              )}

              {/* Scan guide overlay */}
              {scanState === "scanning" && (
                <div className="absolute inset-0 pointer-events-none flex items-center justify-center">
                  <div className="w-52 h-52 rounded-xl border-2 border-sky-400/50">
                    {/* Corner markers */}
                    {["top-0 left-0", "top-0 right-0", "bottom-0 left-0", "bottom-0 right-0"].map((pos, i) => (
                      <div
                        key={i}
                        className={`absolute w-6 h-6 border-sky-400 ${pos} ${
                          ["border-t-2 border-l-2 rounded-tl-sm",
                           "border-t-2 border-r-2 rounded-tr-sm",
                           "border-b-2 border-l-2 rounded-bl-sm",
                           "border-b-2 border-r-2 rounded-br-sm"][i]
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Controls */}
            <div className="px-5 py-4 flex flex-col gap-3">
              {scanState === "idle" || scanState === "error" ? (
                <button
                  onClick={startCamera}
                  className="w-full bg-sky-600 hover:bg-sky-500 text-white font-semibold text-sm py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
                  data-testid="start-scan-btn"
                >
                  <Camera className="w-4 h-4" />
                  Start Scanner
                </button>
              ) : scanState === "scanning" ? (
                <button
                  onClick={() => { stopCamera(); setScanState("idle"); }}
                  className="w-full bg-slate-700 hover:bg-slate-600 text-white font-semibold text-sm py-3 rounded-xl flex items-center justify-center gap-2 transition-colors"
                  data-testid="stop-scan-btn"
                >
                  <RefreshCw className="w-4 h-4" />
                  Cancel
                </button>
              ) : null}

              {/* Manual fallback */}
              <div className="flex items-center gap-2">
                <div className="flex-1 h-px bg-white/10" />
                <span className="text-slate-600 text-[10px] uppercase tracking-wide">or paste token</span>
                <div className="flex-1 h-px bg-white/10" />
              </div>
              <ManualInput onSubmit={verifyToken} loading={verifying} />
            </div>
          </div>

          {/* Audit history (collapsible) */}
          <AuditHistory />
        </>
      )}
      </>
      )}
    </div>
  );
}
